// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md
import toMillis from '../dataHelpers/matrixDataHelpers/toMillis'
import calculateParticipationImpact from './calculateParticipationImpact'
import resolveInterventionDose from '../msiMonetisationHelpers/resolveInterventionDose'

/**
 * Periode-scoping van de participatieladder-teller (methodiek § 1,
 * cohortprincipe): de gemeten verandering bínnen de meetperiode, niet de
 * cumulatieve verandering sinds de start van het traject.
 *
 * Bron is de `ParticipationMeasurements`-log (dag-snapshots, besluit D4).
 */

/**
 * @typedef {object} PeriodeProfielen
 * @property {object} baselineProfile - Profiel aan het begin van de periode: de laatste meting vóór periodestart, of het t0-profiel bij instroom binnen de periode
 * @property {object} endProfile - Profiel van de laatste meting binnen de periode
 * @property {number|null} baselineMs - Datum van het beginprofiel (epoch-ms), null als die onbekend is
 * @property {number} endMs - Datum van het eindprofiel (epoch-ms)
 */

/**
 * Kiest per persona de twee profielen die de periode-delta bepalen.
 * Geen meting binnen de periode → null: niet gemeten in de periode is niet
 * in de teller (de dekking-indicator maakt dat zichtbaar, § 5.1).
 *
 * @param {object} input
 * @param {Array<{PersonaID: string, Profile?: object, Timestamp?: *}>} input.measurements - Alle metingen van de tenant (mag ongefilterd en ongesorteerd)
 * @param {{ ID: string, Participation?: { t0?: object } }} input.persona
 * @param {number} input.startMs - Periodestart (epoch-ms, inclusief)
 * @param {number} input.endMs - Periode-einde (epoch-ms, inclusief)
 * @returns {PeriodeProfielen|null}
 */
const selectPeriodProfiles = ({ measurements, persona, startMs, endMs } = {}) => {
  if (!persona?.ID || !Array.isArray(measurements)) return null

  const eigen = measurements
    .filter((m) => m?.PersonaID === persona.ID)
    .map((m) => ({ profile: m.Profile || {}, ms: toMillis(m.Timestamp) }))
    .filter((m) => Number.isFinite(m.ms))
    .sort((a, b) => a.ms - b.ms)

  const binnenPeriode = eigen.filter((m) => m.ms >= startMs && m.ms <= endMs)
  if (binnenPeriode.length === 0) return null

  const voorPeriode = eigen.filter((m) => m.ms < startMs)
  const baseline = voorPeriode.length > 0
    ? voorPeriode[voorPeriode.length - 1]
    : { profile: persona.Participation?.t0 || {}, ms: toMillis(persona.Participation?.t0?.updatedAt) ?? null }
  const einde = binnenPeriode[binnenPeriode.length - 1]

  return {
    baselineProfile: baseline.profile,
    endProfile: einde.profile,
    // Meetdatums, voor de leeftijdsovergang van parameterset 2026.3.
    baselineMs: baseline.ms,
    endMs: einde.ms,
  }
}

/**
 * @typedef {object} PeriodeTeller
 * @property {number} value - Som van de periode-delta's in euro's
 * @property {number} linkedMeasurementCount - Aantal personas met een gekoppelde voor/na-meting in de periode die doorgerekend kon worden (telt mee voor de ≥ 20-drempel, D5)
 * @property {number} zonderMetingInPeriode - Personas zonder enige meting binnen het boekjaar
 * @property {number} zonderHoofdactiviteit - Personas met een meting in het boekjaar waarvan het eindprofiel geen hoofdactiviteit heeft
 * @property {number} attributedValue - Som van de toegerekende waarden (rekenregels MSI) van wie een horizon heeft
 * @property {number} notAttributedCount - Gekoppelde metingen zonder horizon (duur of uren onbekend). Die voeden de teller niet en tellen dus NIET in linkedMeasurementCount — elke persona valt op precies één van de vier tellers
 * @property {Object<string, number>} notAttributedReasons - Dezelfde telling per reden
 */

const LEGE_TELLER = {
  value: 0, linkedMeasurementCount: 0, zonderMetingInPeriode: 0, zonderHoofdactiviteit: 0,
  attributedValue: 0, notAttributedCount: 0, notAttributedReasons: {},
}

/**
 * Somt de participatieladder-waarde over de meetperiode. Zelfde
 * mainActivity-gate als de live kaart (sumCurrentSocietalValue): een leeg
 * eindprofiel zou een fantoombesparing tonen en telt dus niet mee.
 *
 * DRIE UITKOMSTEN, DRIE TELLERS. Een deelnemer telt mee, of valt af omdat hij
 * dit boekjaar niet gemeten is, of omdat zijn eindprofiel geen hoofdactiviteit
 * heeft. Boek je die twee afwijzingen niet apart, dan werkt de poort goed maar
 * is voor niemand meer te zien wat er ontbreekt — en de detailpagina kan het
 * verschil dan niet benoemen. Elke persona valt op precies één van de drie.
 * AI-CONTEXT: See docs/pitfalls.md#dev-gate-counter-hides-its-own-policy
 *
 * @param {object} input
 * @param {Array<object>} input.personas - Actieve personas van de tenant
 * @param {Array<object>} input.measurements - Alle `ParticipationMeasurements` van de tenant
 * @param {number} input.startMs - Periodestart (epoch-ms)
 * @param {number} input.endMs - Periode-einde (epoch-ms)
 * @param {object} input.parameters - Meetstandaard-rekenparameters
 * @param {Object<string, string>|null} [input.birthDates] - PersonaID → geboortedatum, voor de leeftijdsovergang van 2026.3
 * @param {{ Months?: *, HoursPerWeek?: * }|null} [input.orgDefault] - CompagnyMeta.DefaultInterventionDose (rekenregels MSI)
 * @returns {PeriodeTeller}
 */
const sumParticipationPeriodValue = ({ personas, measurements, startMs, endMs, parameters, birthDates = null, orgDefault = null } = {}) => {
  if (!Array.isArray(personas)) return { ...LEGE_TELLER }

  return personas.reduce((acc, persona) => {
    const profielen = selectPeriodProfiles({ measurements, persona, startMs, endMs })
    if (!profielen) return { ...acc, zonderMetingInPeriode: acc.zonderMetingInPeriode + 1 }
    if (!profielen.endProfile.mainActivity) return { ...acc, zonderHoofdactiviteit: acc.zonderHoofdactiviteit + 1 }

    // Rekenregels MSI: eind van een traject zonder einddatum = de eind-meting van deze periode
    const dose = resolveInterventionDose({ persona, lastMeasurementMs: profielen.endMs, orgDefault })
    const { delta } = calculateParticipationImpact({
      participantProfileT0: profielen.baselineProfile,
      participantProfileT1: profielen.endProfile,
      parameters,
      context: { birthDate: birthDates?.[persona.ID], t0Date: profielen.baselineMs, t1Date: profielen.endMs },
      horizon: dose.horizon,
    })

    const attributed = delta.attributedSocietalValue
    return {
      ...acc,
      value: acc.value + (Number.isFinite(delta.totalSocietalValue) ? delta.totalSocietalValue : 0),
      linkedMeasurementCount: acc.linkedMeasurementCount + (attributed === null ? 0 : 1),
      attributedValue: acc.attributedValue + (attributed === null ? 0 : attributed),
      notAttributedCount: acc.notAttributedCount + (attributed === null ? 1 : 0),
      notAttributedReasons: attributed === null
        ? { ...acc.notAttributedReasons, [dose.reason]: (acc.notAttributedReasons[dose.reason] || 0) + 1 }
        : acc.notAttributedReasons,
    }
  }, { ...LEGE_TELLER })
}

export default selectPeriodProfiles
export { sumParticipationPeriodValue }
